/**
 * sf-gallery-filter.js
 *
 * Filters the product media gallery by the selected "Tenue" option (Domicile /
 * Extérieur / Third). When a tenue is selected, only the media tagged with that
 * tenue (via data-tenue on slides, grid items and thumbnails) stays visible.
 *
 * Initial state / no distinct tenue selected => everything is visible.
 *
 * Works alongside the Horizon slideshow/grid without rebuilding it: it only
 * toggles a `.sf-tenue-hidden` class (display:none) and re-snaps the carousel
 * to the first visible slide. The zoom dialog, thumbnails and grid follow the
 * same rule.
 *
 * The "Tenue" option is detected on the variant picker by the input/select
 * name starting with "Tenue". The filter reacts to both the native `change`
 * event (fires on every click, even for unavailable combos) and the theme's
 * `variant:update` event.
 */
(function () {
  'use strict';

  const HIDDEN_CLASS = 'sf-tenue-hidden';
  const TENUE_OPTION_PREFIX = 'tenue';

  /**
   * Normalize a raw value into a tenue token matching the Liquid data-tenue.
   * @param {string} value
   * @returns {string} 'domicile' | 'exterieur' | 'third'
   */
  function normalizeTenue(value) {
    const v = (value || '')
      .toString()
      .toLowerCase()
      // strip accents (Extérieur -> exterieur)
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .trim();
    if (v.indexOf('exterieur') !== -1) return 'exterieur';
    if (v.indexOf('third') !== -1) return 'third';
    if (v.indexOf('domicile') !== -1) return 'domicile';
    return '';
  }

  /**
   * Read the currently selected Tenue value from a variant picker.
   * @param {Element} picker
   * @returns {string} normalized tenue token, or '' if none
   */
  function getSelectedTenue(picker) {
    if (!picker) return '';

    // Radio buttons: name starts with "Tenue"
    const checkedRadio = picker.querySelector(
      'input[type="radio"][name^="Tenue" i]:checked, input[type="radio"][name^="tenue" i]:checked'
    );
    if (checkedRadio) return normalizeTenue(checkedRadio.value);

    // Dropdown: select inside an option named Tenue
    const selects = picker.querySelectorAll('select[name*="Tenue" i], select[name*="tenue" i]');
    for (const select of selects) {
      if (select.value) return normalizeTenue(select.value);
    }

    // Fallback: scan any select whose associated label/name references Tenue
    return '';
  }

  /**
   * Does this gallery actually have more than one distinct tenue?
   * If not, filtering is a no-op (show everything).
   * @param {Element} gallery
   * @returns {boolean}
   */
  function hasMultipleTenues(gallery) {
    const tokens = new Set();
    gallery.querySelectorAll('[data-tenue]').forEach((el) => {
      const t = el.getAttribute('data-tenue');
      if (t) tokens.add(t);
    });
    return tokens.size > 1;
  }

  /**
   * Apply the filter to a single gallery element.
   * @param {Element} gallery
   * @param {string} tenue normalized token, or '' to show all
   */
  function applyFilter(gallery, tenue) {
    if (!gallery) return;

    const showAll = !tenue || !hasMultipleTenues(gallery);

    gallery.querySelectorAll('[data-tenue]').forEach((el) => {
      const elTenue = el.getAttribute('data-tenue');
      const hide = !showAll && elTenue !== tenue;
      el.classList.toggle(HIDDEN_CLASS, hide);
    });

    // Re-snap the carousel/slideshow to the first visible slide so we never
    // sit on a hidden slide.
    snapToFirstVisible(gallery);
  }

  /**
   * Ensure the slideshow is showing a visible slide.
   * @param {Element} gallery
   */
  function snapToFirstVisible(gallery) {
    const slideshow = gallery.querySelector('slideshow-component');
    if (!slideshow) return;

    const slides = Array.from(slideshow.querySelectorAll('slideshow-slide'));
    if (!slides.length) return;

    const currentVisible =
      slides[gallery.__sfCurrentIndex || 0] &&
      !slides[gallery.__sfCurrentIndex || 0].classList.contains(HIDDEN_CLASS);

    if (currentVisible) return;

    const firstVisibleIndex = slides.findIndex((s) => !s.classList.contains(HIDDEN_CLASS));
    if (firstVisibleIndex < 0) return;

    gallery.__sfCurrentIndex = firstVisibleIndex;

    // Prefer the component's own select() so internal state stays consistent.
    if (typeof slideshow.select === 'function') {
      try {
        slideshow.select(firstVisibleIndex, undefined, { animate: false });
        return;
      } catch (e) {
        /* fall through to manual scroll */
      }
    }

    const target = slides[firstVisibleIndex];
    if (target && typeof target.scrollIntoView === 'function') {
      target.scrollIntoView({ behavior: 'auto', inline: 'start', block: 'nearest' });
    }
  }

  /**
   * Find the gallery(ies) related to a given variant picker (same product section).
   * @param {Element} picker
   * @returns {Element[]}
   */
  function galleriesForPicker(picker) {
    // Galleries live in the same section as the variant picker.
    const section =
      picker.closest('.shopify-section') || picker.closest('[id^="shopify-section"]') || document;
    const galleries = Array.from(section.querySelectorAll('media-gallery'));
    return galleries.length ? galleries : Array.from(document.querySelectorAll('media-gallery'));
  }

  /**
   * Handle a tenue change originating from a variant picker.
   * @param {Element} picker
   */
  function handlePicker(picker) {
    const tenue = getSelectedTenue(picker);
    galleriesForPicker(picker).forEach((gallery) => applyFilter(gallery, tenue));
  }

  /**
   * Wire up listeners on a variant picker if it controls a Tenue option.
   * @param {Element} picker
   */
  function initPicker(picker) {
    if (!picker || picker.__sfTenueBound) return;

    const hasTenue =
      picker.querySelector('input[name^="Tenue" i], input[name^="tenue" i]') ||
      picker.querySelector('select[name*="Tenue" i], select[name*="tenue" i]');
    if (!hasTenue) return;

    picker.__sfTenueBound = true;

    picker.addEventListener('change', () => handlePicker(picker));

    // Initial sync (covers a pre-selected Extérieur/Third on load).
    handlePicker(picker);
  }

  function init() {
    document.querySelectorAll('variant-picker').forEach(initPicker);

    // Theme variant update (also fires on combined-listing / section re-render).
    document.addEventListener('variant:update', (event) => {
      const target =
        (event.target && event.target.closest && event.target.closest('variant-picker')) ||
        document.querySelector('variant-picker');
      if (target) {
        initPicker(target);
        handlePicker(target);
      } else {
        document.querySelectorAll('variant-picker').forEach(handlePicker);
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-init when sections are reloaded by the theme editor.
  document.addEventListener('shopify:section:load', () => {
    document.querySelectorAll('variant-picker').forEach(initPicker);
  });
})();
